import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Save, SlidersHorizontal } from "lucide-react";
import { api } from "../../lib/api";
import type { OrganizationSetting, OrganizationSettingsResponse, UpdateOrganizationSettingRequest } from "../../lib/types";
import { labelFromCode } from "../shared/format";
const errorMessage = (error: unknown) => error instanceof Error ? error.message : "Could not save settings.";

function settingDraftValue(setting: OrganizationSetting) {
  if (setting.value_type === "NUMBER") {
    return setting.number_value === null ? "" : String(setting.number_value);
  }
  if (setting.value_type === "JSON") {
    return setting.json_value ?? "";
  }
  return setting.string_value ?? "";
}

export default function OrganizationSettingsEditor({ organizationId, organizationType }: { organizationId: string; organizationType?: string }) {
  const queryClient = useQueryClient();
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savedSettingKey, setSavedSettingKey] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<{ settingKey: string; message: string } | null>(null);
  const queryKey = ["tech-admin-organization-settings", organizationId] as const;
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () => api.getAdminOrganizationSettings(organizationId)
  });
  const groups = settingsQuery.data?.groups ?? [];

  useEffect(() => {
    if (!settingsQuery.data) return;
    const nextDrafts: Record<string, string> = {};
    for (const group of settingsQuery.data.groups) {
      for (const setting of group.settings) {
        if (setting.value_type !== "BOOLEAN") {
          nextDrafts[setting.setting_key] = settingDraftValue(setting);
        }
      }
    }
    setDraftValues(nextDrafts);
  }, [organizationId, settingsQuery.data]);

  useEffect(() => {
    setSavedSettingKey(null);
    setValidationError(null);
  }, [organizationId]);

  const updateMutation = useMutation({
    mutationFn: ({ settingKey, body }: { settingKey: string; body: UpdateOrganizationSettingRequest }) => (
      api.updateAdminOrganizationSetting(organizationId, settingKey, body)
    ),
    onSuccess(data, variables) {
      queryClient.setQueryData<OrganizationSettingsResponse>(queryKey, data);
      setSavedSettingKey(variables.settingKey);
      window.setTimeout(() => setSavedSettingKey(null), 2200);
    }
  });

  function updateSetting(settingKey: string, body: UpdateOrganizationSettingRequest) {
    setSavedSettingKey(null);
    setValidationError(null);
    updateMutation.mutate({ settingKey, body });
  }

  function saveSetting(setting: OrganizationSetting) {
    const draft = (draftValues[setting.setting_key] ?? "").trim();
    if (!draft) {
      setValidationError({ settingKey: setting.setting_key, message: "Enter a value before saving." });
      return;
    }
    if (setting.value_type === "STRING") {
      updateSetting(setting.setting_key, { string_value: draft });
      return;
    }
    if (setting.value_type === "NUMBER") {
      const numberValue = Number(draft);
      if (!Number.isFinite(numberValue)) {
        setValidationError({ settingKey: setting.setting_key, message: "Enter a valid number." });
        return;
      }
      updateSetting(setting.setting_key, { number_value: numberValue });
      return;
    }
    try {
      JSON.parse(draft);
      updateSetting(setting.setting_key, { json_value: draft });
    } catch {
      setValidationError({ settingKey: setting.setting_key, message: "Enter valid JSON before saving." });
    }
  }

  return (
    <section className="pa-organization-settings" aria-labelledby="organization-settings-heading">
      <div className="pa-organization-settings-heading">
        <div>
          <h3 id="organization-settings-heading"><SlidersHorizontal size={16} /> Organization settings</h3>
          <p>Control workflows and terminology for this organization. Available settings depend on its saved organization type.</p>
        </div>
      </div>

      {settingsQuery.isPending ? <p className="pa-organization-settings-state">Loading settings...</p> : null}
      {settingsQuery.isError ? (
        <div className="pa-form-error" role="alert">{errorMessage(settingsQuery.error)} <button className="pa-secondary-button" onClick={() => void settingsQuery.refetch()}>Retry settings</button></div>
      ) : null}
      {!settingsQuery.isPending && !settingsQuery.isError && groups.length === 0 ? (
        <p className="pa-organization-settings-state">No configurable settings are available{organizationType ? ` for ${organizationType === "SMB" ? "SMB" : labelFromCode(organizationType)} organizations` : ""}. If the organization type is incorrect, update it in the Profile tab and save to load the applicable settings.</p>
      ) : null}

      {groups.map((group) => (
        <section className="pa-organization-settings-group" key={group.group_id}>
          <header><strong>{group.name}</strong></header>
          {group.settings.map((setting) => {
            const isSaving = updateMutation.isPending
              && updateMutation.variables?.settingKey === setting.setting_key;
            const settingError = validationError?.settingKey === setting.setting_key
              ? validationError.message
              : updateMutation.isError && updateMutation.variables?.settingKey === setting.setting_key
                ? errorMessage(updateMutation.error)
                : null;
            const draftValue = draftValues[setting.setting_key] ?? "";
            const savedValue = settingDraftValue(setting);
            const hasChanged = draftValue.trim() !== savedValue.trim();

            return (
              <div className="pa-organization-setting-row" key={setting.setting_key}>
                <div className="pa-organization-setting-main">
                  <div className="pa-organization-setting-copy">
                    <strong>{setting.label}</strong>
                    {setting.note ? <small>{setting.note}</small> : null}
                    {savedSettingKey === setting.setting_key ? (
                      <span className="pa-organization-setting-saved"><CheckCircle2 size={13} /> Saved</span>
                    ) : null}
                  </div>
                  {setting.value_type === "BOOLEAN" ? (
                    <button
                      className={setting.boolean_value ? "pa-settings-switch active" : "pa-settings-switch"}
                      type="button"
                      role="switch"
                      aria-checked={Boolean(setting.boolean_value)}
                      aria-label={`${setting.label}: ${setting.boolean_value ? "on" : "off"}`}
                      title={setting.boolean_value ? "Turn off" : "Turn on"}
                      disabled={updateMutation.isPending}
                      onClick={() => updateSetting(setting.setting_key, { boolean_value: !setting.boolean_value })}
                    >
                      <span />
                    </button>
                  ) : null}
                </div>

                {setting.value_type !== "BOOLEAN" ? (
                  <div className="pa-organization-setting-editor">
                    {setting.value_type === "JSON" ? (
                      <textarea
                        value={draftValue}
                        rows={3}
                        maxLength={setting.max_length ?? undefined}
                        aria-label={setting.label}
                        onChange={(event) => setDraftValues((current) => ({
                          ...current,
                          [setting.setting_key]: event.target.value
                        }))}
                      />
                    ) : (
                      <input
                        type={setting.value_type === "NUMBER" ? "number" : "text"}
                        value={draftValue}
                        maxLength={setting.max_length ?? undefined}
                        step={setting.value_type === "NUMBER" ? "any" : undefined}
                        aria-label={setting.label}
                        onChange={(event) => setDraftValues((current) => ({
                          ...current,
                          [setting.setting_key]: event.target.value
                        }))}
                      />
                    )}
                    <button
                      className="pa-secondary-button pa-setting-save-button"
                      type="button"
                      disabled={updateMutation.isPending || !draftValue.trim() || !hasChanged}
                      onClick={() => saveSetting(setting)}
                    >
                      <Save size={14} /> {isSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                ) : null}
                {settingError ? <div className="pa-setting-inline-error">{settingError}</div> : null}
              </div>
            );
          })}
        </section>
      ))}
    </section>
  );
}

